import { memo, useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { AccountStatusBadgeMemo } from './AccountStatusBadge';
import { IAccountCardProps } from '@/@types/socialMedia';
import { cn } from '@/lib/utils';

/**
 * Card displaying a single connected social media account
 */
const AccountCard = ({ account, onDisconnect, onReconnect }: IAccountCardProps) => {
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const handleDisconnect = useCallback(async () => {
    setIsDisconnecting(true);
    try {
      await onDisconnect(account._id);
    } finally {
      setIsDisconnecting(false);
    }
  }, [account._id, onDisconnect]);

  const handleReconnect = useCallback(() => {
    if (onReconnect) {
      onReconnect(account._id);
    }
  }, [account._id, onReconnect]);

  const formattedConnectedDate = new Date(account.connectedAt).toLocaleDateString();
  const needsReconnect = account.status === 'expired' || account.status === 'reconnect_required';

  return (
    <div className="border rounded-lg p-4 bg-card">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          {/* Platform Icon */}
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-lg bg-blue-50 dark:bg-blue-900/40 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-blue-600 dark:text-blue-400"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </div>
          </div>

          {/* Account Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <h3 className="text-sm font-semibold text-foreground truncate">
                {account.accountName}
              </h3>
              <AccountStatusBadgeMemo status={account.status} />
            </div>

            <p className="text-sm text-muted-foreground mt-1">
              Facebook Page
              {account.accountCategory && ` • ${account.accountCategory}`}
            </p>

            <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
              <span>Connected {formattedConnectedDate}</span>
              {account.totalPublishedPosts > 0 && (
                <span>{account.totalPublishedPosts} posts published</span>
              )}
              {account.accountFollowerCount !== undefined && (
                <span>{account.accountFollowerCount.toLocaleString()} followers</span>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-2">
          {needsReconnect && onReconnect && (
            <Button onClick={handleReconnect} size="sm" variant="outline">
              Reconnect
            </Button>
          )}
          <Button
            onClick={handleDisconnect}
            disabled={isDisconnecting}
            size="sm"
            variant="ghost"
            className={cn(
              'text-red-600 hover:text-red-700 hover:bg-red-50',
              'dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20'
            )}
          >
            {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const AccountCardMemo = memo(AccountCard);
